import { useState, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  ArrowLeft,
  Sun,
  Moon,
  Monitor,
  Camera,
  Upload,
  Loader2,
  X,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { useIsMobile } from "@/hooks/useMobileUtils";
import { LANGUAGES } from "@/i18n";
import { cn } from "@/lib/utils";
import { authApi } from "@/services/api";
import { useAuthStore } from "@/store/auth.store";
import { useThemeStore } from "@/store/theme.store";

export const Profile = () => {
  const { t, i18n } = useTranslation();
  const { user, updateUser } = useAuthStore();
  const { theme, setTheme } = useThemeStore();
  const isMobile = useIsMobile();

  const [profile, setProfile] = useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    phone: user?.phone || "",
  });

  const [preferences, setPreferences] = useState({
    timezone: user?.timezone || "Australia/Sydney",
    locale: user?.locale || "en-AU",
    dateFormat: user?.dateFormat || "DD/MM/YYYY",
    timeFormat: user?.timeFormat || "24h",
  });

  // Avatar state
  const avatarInputRef = useRef(null);

  const userInitials = `${user?.firstName?.[0] || ""}${
    user?.lastName?.[0] || ""
  }`.toUpperCase();

  // ============================================
  // TanStack Query - Mutations
  // ============================================

  // Upload avatar mutation
  const uploadAvatarMutation = useMutation({
    mutationFn: (file) => authApi.uploadAvatar(file),
    onSuccess: (response) => {
      updateUser(response.data.data);
      toast.success(t("settings.profile.avatarUploaded"));
      // Reset input
      if (avatarInputRef.current) {
        avatarInputRef.current.value = "";
      }
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error?.message ||
          t("settings.profile.avatarUploadError"),
      );
    },
  });

  // Delete avatar mutation
  const deleteAvatarMutation = useMutation({
    mutationFn: () => authApi.deleteAvatar(),
    onSuccess: (response) => {
      updateUser(response.data.data);
      toast.success(t("settings.profile.avatarRemoved"));
    },
    onError: () => {
      toast.error(t("settings.profile.avatarRemoveError"));
    },
  });

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: (profileData) => authApi.updateProfile(profileData),
    onSuccess: (response) => {
      updateUser(response.data.data);
      toast.success(t("settings.profile.saved"));
    },
    onError: () => {
      toast.error(t("errors.generic"));
    },
  });

  // Update preferences mutation
  const updatePreferencesMutation = useMutation({
    mutationFn: (prefsData) => authApi.updateProfile(prefsData),
    onSuccess: (response, variables) => {
      updateUser(response.data.data);

      // Apply locale change immediately and persist to localStorage
      if (variables.locale && variables.locale !== i18n.language) {
        i18n.changeLanguage(variables.locale);
        localStorage.setItem("i18nextLng", variables.locale);
      }

      toast.success(t("settings.preferences.saved"));
    },
    onError: () => {
      toast.error(t("settings.preferences.saveError"));
    },
  });

  // Derived states
  const uploadingAvatar =
    uploadAvatarMutation.isPending || deleteAvatarMutation.isPending;
  const loading =
    updateProfileMutation.isPending || updatePreferencesMutation.isPending;

  // Handle avatar upload
  const handleAvatarUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t("settings.profile.avatarInvalidType"));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t("settings.profile.avatarTooLarge"));
      return;
    }

    uploadAvatarMutation.mutate(file);
  };

  // Handle avatar delete
  const handleAvatarDelete = () => {
    deleteAvatarMutation.mutate();
  };

  const timezones = [
    { value: "Australia/Sydney", label: t("timezones.sydney") },
    { value: "Australia/Melbourne", label: t("timezones.melbourne") },
    { value: "Australia/Brisbane", label: t("timezones.brisbane") },
    { value: "Australia/Perth", label: t("timezones.perth") },
    { value: "Pacific/Auckland", label: t("timezones.auckland") },
    { value: "UTC", label: t("timezones.utc") },
    { value: "America/New_York", label: t("timezones.eastern") },
    { value: "America/Chicago", label: t("timezones.central") },
    { value: "America/Denver", label: t("timezones.mountain") },
    { value: "America/Los_Angeles", label: t("timezones.pacific") },
    { value: "Europe/London", label: t("timezones.london") },
    { value: "Europe/Paris", label: t("timezones.paris") },
    { value: "Europe/Berlin", label: t("timezones.berlin") },
    { value: "Asia/Tokyo", label: t("timezones.tokyo") },
    { value: "Asia/Shanghai", label: t("timezones.shanghai") },
    { value: "Asia/Singapore", label: t("timezones.singapore") },
  ];

  const dateFormats = [
    { value: "DD/MM/YYYY", label: "04/12/2025", example: "04/12/2025" },
    { value: "D MMM YYYY", label: "4 Dec 2025", example: "4 Dec 2025" },
    {
      value: "D MMMM YYYY",
      label: "4 December 2025",
      example: "4 December 2025",
    },
    { value: "YYYY-MM-DD", label: "2025-12-04", example: "2025-12-04" },
    { value: "MM/DD/YYYY", label: "12/04/2025", example: "12/04/2025" },
    { value: "MMM D, YYYY", label: "Dec 4, 2025", example: "Dec 4, 2025" },
    {
      value: "MMMM D, YYYY",
      label: "December 4, 2025",
      example: "December 4, 2025",
    },
  ];

  const timeFormats = [
    { value: "24h", label: t("timeFormats.24hour") },
    { value: "12h", label: t("timeFormats.12hour") },
  ];

  const handleProfileUpdate = (e) => {
    e.preventDefault();
    updateProfileMutation.mutate(profile);
  };

  const handlePreferencesUpdate = (e) => {
    e.preventDefault();
    updatePreferencesMutation.mutate({
      timezone: preferences.timezone,
      locale: preferences.locale,
      dateFormat: preferences.dateFormat,
      timeFormat: preferences.timeFormat,
    });
  };

  return (
    <div className="space-y-6 pb-20 sm:pb-0">
      {/* Page header with back link */}
      <div className="flex items-center gap-4">
        <Link
          to="/settings"
          className="flex items-center justify-center h-10 w-10 sm:h-8 sm:w-8 rounded-lg border hover:bg-accent touch-target"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("settings.profile.title")}
          </h1>
          <p className="text-muted-foreground">
            {t("settings.profile.description")}
          </p>
        </div>
      </div>

      {/* Profile Information */}
      <Card>
        <CardHeader>
          <CardTitle>{t("settings.profile.personalInfo")}</CardTitle>
          <CardDescription>
            {t("settings.profile.personalInfoDesc")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6 mb-6 pb-6 border-b">
            <div className="relative group">
              <Avatar className="h-24 w-24">
                {user?.avatarUrl ? (
                  <AvatarImage
                    src={user.avatarUrl}
                    alt={`${user.firstName} ${user.lastName}`}
                  />
                ) : null}
                <AvatarFallback className="text-2xl bg-primary text-primary-foreground">
                  {userInitials}
                </AvatarFallback>
              </Avatar>
              {uploadingAvatar && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
                  <Loader2 className="h-6 w-6 animate-spin text-white" />
                </div>
              )}
              <button
                onClick={() => avatarInputRef.current?.click()}
                disabled={uploadingAvatar}
                className="absolute bottom-0 right-0 p-1.5 bg-primary text-primary-foreground rounded-full shadow-lg hover:bg-primary/90 transition-colors"
              >
                <Camera className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1">
              <h3 className="font-medium mb-1">
                {t("settings.profile.avatar")}
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                {t("settings.profile.avatarDescription")}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => avatarInputRef.current?.click()}
                  disabled={uploadingAvatar}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {t("settings.profile.uploadPhoto")}
                </Button>
                {user?.avatarUrl && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleAvatarDelete}
                    disabled={uploadingAvatar}
                    className="text-destructive hover:text-destructive"
                  >
                    <X className="h-4 w-4 mr-2" />
                    {t("settings.profile.removePhoto")}
                  </Button>
                )}
              </div>
              <input
                ref={avatarInputRef}
                aria-label="Upload avatar"
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleAvatarUpload}
                className="hidden"
              />
            </div>
          </div>

          <form onSubmit={handleProfileUpdate} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="firstName">
                  {t("settings.profile.firstName")}
                </Label>
                <Input
                  id="firstName"
                  value={profile.firstName}
                  onChange={(e) =>
                    setProfile((p) => ({ ...p, firstName: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">
                  {t("settings.profile.lastName")}
                </Label>
                <Input
                  id="lastName"
                  value={profile.lastName}
                  onChange={(e) =>
                    setProfile((p) => ({ ...p, lastName: e.target.value }))
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t("settings.profile.email")}</Label>
              <Input
                id="email"
                type="email"
                value={user?.email || ""}
                disabled
                className="bg-muted"
              />
              <p className="text-xs text-muted-foreground">
                {t("settings.profile.emailHelp")}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">{t("settings.profile.phone")}</Label>
              <Input
                id="phone"
                type="tel"
                value={profile.phone}
                onChange={(e) =>
                  setProfile((p) => ({ ...p, phone: e.target.value }))
                }
                placeholder={t("settings.profile.phonePlaceholder")}
              />
            </div>

            <div className="pt-4">
              <Button
                type="submit"
                disabled={loading}
                size={isMobile ? "mobile" : "default"}
                className={isMobile ? "w-full" : ""}
              >
                {loading ? t("common.saving") : t("common.saveChanges")}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Preferences */}
      <Card>
        <CardHeader>
          <CardTitle>{t("settings.preferences.title")}</CardTitle>
          <CardDescription>
            {t("settings.preferences.description")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePreferencesUpdate} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="timezone">
                  {t("settings.preferences.timezone")}
                </Label>
                <Select
                  value={preferences.timezone}
                  onValueChange={(value) =>
                    setPreferences((p) => ({ ...p, timezone: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t("settings.preferences.timezone")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {timezones.map((tz) => (
                      <SelectItem key={tz.value} value={tz.value}>
                        {tz.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="locale">
                  {t("settings.preferences.language")}
                </Label>
                <Select
                  value={preferences.locale}
                  onValueChange={(value) =>
                    setPreferences((p) => ({ ...p, locale: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t("settings.preferences.language")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGUAGES.map((lang) => (
                      <SelectItem key={lang.code} value={lang.code}>
                        <span className="flex items-center gap-2">
                          <span>{lang.flag}</span>
                          <span>{lang.nativeName}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="dateFormat">
                  {t("settings.preferences.dateFormat")}
                </Label>
                <Select
                  value={preferences.dateFormat}
                  onValueChange={(value) =>
                    setPreferences((p) => ({ ...p, dateFormat: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t("settings.preferences.dateFormat")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {dateFormats.map((df) => (
                      <SelectItem key={df.value} value={df.value}>
                        {df.example}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="timeFormat">
                  {t("settings.preferences.timeFormat")}
                </Label>
                <Select
                  value={preferences.timeFormat}
                  onValueChange={(value) =>
                    setPreferences((p) => ({ ...p, timeFormat: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t("settings.preferences.timeFormat")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {timeFormats.map((tf) => (
                      <SelectItem key={tf.value} value={tf.value}>
                        {tf.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Separator className="my-4" />

            <div className="space-y-3">
              <Label>{t("settings.preferences.theme")}</Label>
              <p className="text-xs text-muted-foreground">
                {t("settings.preferences.themeHint")}
              </p>
              <div className="grid grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => setTheme("light")}
                  className={cn(
                    "flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-colors",
                    theme === "light"
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50",
                  )}
                >
                  <Sun className="h-5 w-5" />
                  <span className="text-sm font-medium">
                    {t("settings.preferences.themeLight")}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setTheme("dark")}
                  className={cn(
                    "flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-colors",
                    theme === "dark"
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50",
                  )}
                >
                  <Moon className="h-5 w-5" />
                  <span className="text-sm font-medium">
                    {t("settings.preferences.themeDark")}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setTheme("system")}
                  className={cn(
                    "flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-colors",
                    theme === "system"
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50",
                  )}
                >
                  <Monitor className="h-5 w-5" />
                  <span className="text-sm font-medium">
                    {t("settings.preferences.themeSystem")}
                  </span>
                </button>
              </div>
            </div>

            <div className="pt-4">
              <Button
                type="submit"
                disabled={loading}
                size={isMobile ? "mobile" : "default"}
                className={isMobile ? "w-full" : ""}
              >
                {loading ? t("common.saving") : t("common.saveChanges")}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Profile;
